/**
 * Service Form Dialog (Add/Edit)
 *
 * Displays common fields (Service ID, Status, Upstream Carrier, Notes) plus type-specific fields:
 * - NBN: WSM Service ID, NBN Carrier ID, NBN AVC, NBN POI
 * - Sonweb: Primary Domain, Web Server, Web Hosting Plan, Panel Type
 * - Other types: common fields only (mobile, voice, data, supa_networks)
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DriveFileMove as MoveIcon } from '@mui/icons-material';
import {
  AccountService,
  AccountServiceCreate,
  AccountServiceUpdate,
  HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS,
} from '../types/account';
import apiService from '../services/api';
import { OrderProduct, UpstreamCarrier } from '../types/order';
import {
  VM_ORDER_CATEGORY_SLUG,
  VM_ORDER_SUBCATEGORY_SLUG,
  isVmPlanServiceField,
  vmPlanSelectValue,
} from '../utils/virtualMachinePlan';
import { isDomainNameServiceType, isLikelyFqdn } from '../utils/domainName';

// Fallbacks only: the real lists are the options configured in Settings → Service Types → sonweb
const WEB_SERVERS = [
  { value: '', label: '(Blank)' },
  { value: 'son-web-01.sonicnet.net.au', label: 'son-web-01.sonicnet.net.au' },
  { value: 'son-web-02.sonicnet.net.au', label: 'son-web-02.sonicnet.net.au' },
];

const WEB_HOSTING_PLANS = [
  'Lite 1GB',
  'Entry 2GB',
  'Standard 5GB',
  'Advanced 10GB',
  'Premium 25GB',
  'Premium Plus 50GB',
];

const PANEL_TYPES = [
  { value: '', label: '(Blank)' },
  { value: 'CWP', label: 'CWP' },
  { value: 'cPanel', label: 'cPanel' },
];

const NBN_TECHNOLOGY_TYPES = ['FTTP', 'FTTN', 'FTTB', 'FTTC', 'HFC', 'FW'];
const NBN_AUTHENTICATION_TYPES = ['IPOE', 'PPPoE'];
const NBN_IP_ADDRESS_TYPES = ['CGNAT', 'Dynamic IP', 'Static IP'];
const NBN_MAPPING_OPTIONS = ['Default', 'Tagged'];
const NBN_UNID_PORTS = ['UNI-D1', 'UNI-D2', 'UNI-D3', 'UNI-D4'];

/** If API fails, keep legacy dropdown so the form still works */
const FALLBACK_SERVICE_TYPE_OPTIONS: { code: string; name: string; sort_order: number }[] = [
  { code: 'nbn', name: 'NBN', sort_order: 10 },
  { code: 'mobile', name: 'Mobile', sort_order: 20 },
  { code: 'voice', name: 'Voice', sort_order: 30 },
  { code: 'data', name: 'Data', sort_order: 40 },
  { code: 'sonweb', name: 'SonWeb', sort_order: 50 },
  { code: 'supa_networks', name: 'Supa Networks', sort_order: 60 },
];

interface ServiceTypeOption {
  id?: string;
  code: string;
  name: string;
  sort_order: number;
}

interface DynamicServiceTypeField {
  id: string;
  field_key: string;
  label: string;
  field_type: string;
  required?: boolean;
  sort_order?: number;
  options?: { id: string; value: string; label?: string | null; sort_order?: number }[];
}

interface ServiceFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  accountId: string;
  service?: AccountService | null;
  /** When editing, opens move-to-account flow (parent closes this dialog and shows picker). */
  onRequestMove?: () => void;
}

const ServiceFormDialog: React.FC<ServiceFormDialogProps> = ({
  open,
  onClose,
  onSave,
  accountId,
  service,
  onRequestMove,
}) => {
  const isEdit = !!service;

  const [formData, setFormData] = useState<Record<string, string>>({
    account_id: accountId,
    service_id: '',
    service_type: 'nbn',
    wsm_service_id: '',
    status: 'active',
    upstream_carrier: '',
    notes: '',
    nbn_carrier_id: '',
    nbn_vanity_identifier: '',
    nbn_avc: '',
    nbn_poi: '',
    nbn_plan_id: '',
    nbn_technology_type: '',
    nbn_authentication_type: '',
    nbn_ip_address_type: '',
    nbn_ip_address: '',
    nbn_service_class: '',
    nbn_sla: '',
    nbn_mapping: '',
    nbn_unid_port: '',
    primary_domain: '',
    web_server: '',
    web_hosting_plan: '',
    panel_type: '',
  });

  const [nbnPlans, setNbnPlans] = useState<{ id: string; name: string }[]>([]);
  const [vmPlanProducts, setVmPlanProducts] = useState<OrderProduct[]>([]);
  const [vmPlanProductsLoading, setVmPlanProductsLoading] = useState(false);
  const [upstreamCarriers, setUpstreamCarriers] = useState<UpstreamCarrier[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceTypeOption[]>(FALLBACK_SERVICE_TYPE_OPTIONS);
  const [dynamicFields, setDynamicFields] = useState<DynamicServiceTypeField[]>([]);
  const [dynamicFieldsLoading, setDynamicFieldsLoading] = useState(false);
  const [configuredOptions, setConfiguredOptions] = useState<Record<string, { value: string; label: string }[]>>({});

  /** Options from Settings if configured (else the built-in list), always including the current value
   * so a service on a since-removed option doesn't silently show blank. */
  const selectOptions = (key: 'web_server' | 'panel_type', fallback: { value: string; label: string }[]) => {
    const list = configuredOptions[key] ?? fallback;
    const current = String(formData[key] || '');
    return current && !list.some((o) => o.value === current) ? [...list, { value: current, label: `${current} (not in settings)` }] : list;
  };
  const [mobilePlans, setMobilePlans] = useState<{ id: string; name: string }[]>([]);
  const [typeSpecific, setTypeSpecific] = useState<Record<string, string>>({});
  // Password-type fields are stored encrypted — typeSpecific holds the raw stored
  // ciphertext until the user actually edits one, but the visible input must never
  // display it. This tracks which password fields the user has started typing into.
  const [touchedPasswordFields, setTouchedPasswordFields] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      apiService.adminListUpstreamCarriers(true).then(setUpstreamCarriers).catch(() => setUpstreamCarriers([]));
      apiService
        .getServiceTypes(false)
        .then((data) => {
          const arr = Array.isArray(data) ? data : [];
          const mapped: ServiceTypeOption[] = arr
            .map((t: { id?: string; code: string; name: string; sort_order?: number }) => ({
              id: t.id,
              code: (t.code || '').trim().toLowerCase(),
              name: t.name || t.code,
              sort_order: typeof t.sort_order === 'number' ? t.sort_order : 0,
            }))
            .filter((t) => t.code)
            .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
          setServiceTypeOptions(mapped.length ? mapped : FALLBACK_SERVICE_TYPE_OPTIONS);
        })
        .catch(() => setServiceTypeOptions(FALLBACK_SERVICE_TYPE_OPTIONS));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (service) {
        setFormData({
          account_id: accountId,
          service_id: service.service_id || '',
          service_type: service.service_type || 'nbn',
          wsm_service_id: service.wsm_service_id || '',
          status: service.status || 'active',
          upstream_carrier: service.upstream_carrier || '',
          notes: service.notes || '',
          nbn_carrier_id: service.nbn_carrier_id || '',
          nbn_vanity_identifier: service.nbn_vanity_identifier || '',
          nbn_avc: service.nbn_avc || '',
          nbn_poi: service.nbn_poi || '',
          nbn_plan_id: service.nbn_plan_id || '',
          nbn_technology_type: service.nbn_technology_type || '',
          nbn_authentication_type: service.nbn_authentication_type || '',
          nbn_ip_address_type: service.nbn_ip_address_type || '',
          nbn_ip_address: service.nbn_ip_address || '',
          nbn_service_class: service.nbn_service_class || '',
          nbn_sla: service.nbn_sla || '',
          nbn_mapping: service.nbn_mapping || '',
          nbn_unid_port: service.nbn_unid_port || '',
          primary_domain: service.primary_domain || '',
          web_server: service.web_server || '',
          web_hosting_plan: service.web_hosting_plan || '',
          panel_type: service.panel_type || '',
        });
      } else {
        setFormData({
          account_id: accountId,
          service_id: '',
          service_type: 'nbn',
          wsm_service_id: '',
          status: 'active',
          upstream_carrier: '',
          notes: '',
          nbn_carrier_id: '',
          nbn_vanity_identifier: '',
          nbn_avc: '',
          nbn_poi: '',
          nbn_plan_id: '',
          nbn_technology_type: '',
          nbn_authentication_type: '',
          nbn_ip_address_type: '',
          nbn_ip_address: '',
          nbn_service_class: '',
          nbn_sla: '',
          nbn_mapping: '',
          nbn_unid_port: '',
          primary_domain: '',
          web_server: '',
          web_hosting_plan: '',
          panel_type: '',
        });
      }
      setError(null);
    }
  }, [open, service, accountId]);

  useEffect(() => {
    if (!open) return;
    const raw = service?.type_specific_data;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const next: Record<string, string> = {};
      Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
        next[k] = v === null || v === undefined ? '' : String(v);
      });
      setTypeSpecific(next);
    } else {
      setTypeSpecific({});
    }
    setTouchedPasswordFields(new Set());
  }, [open, service?.id]);

  useEffect(() => {
    if (!open) {
      setDynamicFields([]);
      setConfiguredOptions({});
      return;
    }
    const opt = serviceTypeOptions.find((o) => o.code === formData.service_type);
    if (!opt?.id) {
      setDynamicFields([]);
      setConfiguredOptions({});
      return;
    }
    setDynamicFieldsLoading(true);
    apiService
      .getServiceType(opt.id)
      .then((t: { fields?: DynamicServiceTypeField[] }) => {
        // Dropdown options configured for the hardcoded Sonweb columns (Web Server, Panel Type)
        const configured: Record<string, { value: string; label: string }[]> = {};
        (t.fields || []).forEach((fld) => {
          if (fld.field_key === 'web_server' || fld.field_key === 'panel_type') {
            const list = [...(fld.options || [])]
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
              .map((o) => ({ value: o.value, label: o.label || o.value || '(Blank)' }));
            if (list.length > 0) configured[fld.field_key] = list;
          }
        });
        setConfiguredOptions(configured);
        // Fields that collide with a hardcoded AccountService column are edited
        // via the dedicated section above instead — don't render them twice.
        const f = [...(t.fields || [])]
          .filter((fld) => !HARDCODED_ACCOUNT_SERVICE_FIELD_KEYS.has(fld.field_key))
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        setDynamicFields(f);
      })
      .catch(() => setDynamicFields([]))
      .finally(() => setDynamicFieldsLoading(false));
  }, [open, formData.service_type, serviceTypeOptions]);

  /** After types load for “add”, ensure selected code exists in the list (prefer nbn, else first). */
  useEffect(() => {
    if (!open || service || serviceTypeOptions.length === 0) return;
    const codes = new Set(serviceTypeOptions.map((o) => o.code));
    if (codes.has(formData.service_type)) return;
    const fallback =
      serviceTypeOptions.find((o) => o.code === 'nbn')?.code ?? serviceTypeOptions[0]?.code ?? 'nbn';
    setFormData((prev) => ({ ...prev, service_type: fallback }));
  }, [open, service, serviceTypeOptions, formData.service_type]);

  useEffect(() => {
    if (open && formData.service_type === 'nbn') {
      apiService.getOrderProducts({ category: 'internet', subcategory: 'nbn', type: 'internet_plan' })
        .then((products) => setNbnPlans(products.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name }))))
        .catch(() => setNbnPlans([]));
    } else {
      setNbnPlans([]);
    }
  }, [open, formData.service_type]);

  useEffect(() => {
    if (!open || formData.service_type !== 'virtual_machine') {
      setVmPlanProducts([]);
      return;
    }
    setVmPlanProductsLoading(true);
    apiService
      .getOrderProducts({
        category: VM_ORDER_CATEGORY_SLUG,
        subcategory: VM_ORDER_SUBCATEGORY_SLUG,
      })
      .then(setVmPlanProducts)
      .catch(() => setVmPlanProducts([]))
      .finally(() => setVmPlanProductsLoading(false));
  }, [open, formData.service_type]);

  useEffect(() => {
    if (open && formData.service_type === 'mobile') {
      apiService.getOrderProducts({ category: 'mobile' })
        .then((products) => setMobilePlans(products.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name }))))
        .catch(() => setMobilePlans([]));
    } else {
      setMobilePlans([]);
    }
  }, [open, formData.service_type]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setFormData((prev) => ({ ...prev, service_type: v }));
    if (!service || v !== service.service_type) {
      setTypeSpecific({});
    }
  };

  const buildTypeSpecificPayload = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const dynamicKeys = new Set(dynamicFields.map((f) => f.field_key));
    // Preserve any existing type_specific_data keys that aren't rendered as a
    // dynamic field here (e.g. hardcoded-column collision keys filtered out
    // of dynamicFields) — otherwise this full-replace save would silently
    // wipe them on the next unrelated edit.
    for (const [key, raw] of Object.entries(typeSpecific)) {
      if (!dynamicKeys.has(key) && raw !== '') out[key] = raw;
    }
    for (const f of dynamicFields) {
      const key = f.field_key;
      const raw = (typeSpecific[key] ?? '').trim();
      const ftype = (f.field_type || 'text').toLowerCase();
      if (f.required && raw === '') {
        throw new Error(`${f.label || key} is required`);
      }
      if (raw === '') continue;
      if (ftype === 'number') {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : raw;
      } else {
        out[key] = raw;
      }
    }
    return out;
  };

  const buildPayload = () => {
    const str = (v: string) => (v?.trim() || undefined);
    return {
      service_id: str(formData.service_id),
      service_type: formData.service_type,
      status: formData.status as AccountService['status'],
      upstream_carrier: str(formData.upstream_carrier) || undefined,
      notes: formData.notes?.trim() || null,
      ...(formData.service_type === 'nbn' && {
        wsm_service_id: str(formData.wsm_service_id) || undefined,
        nbn_carrier_id: str(formData.nbn_carrier_id) || undefined,
        nbn_vanity_identifier: str(formData.nbn_vanity_identifier) || undefined,
        nbn_avc: str(formData.nbn_avc) || undefined,
        nbn_poi: str(formData.nbn_poi) || undefined,
        nbn_plan_id: str(formData.nbn_plan_id) || undefined,
        nbn_technology_type: str(formData.nbn_technology_type) || undefined,
        nbn_authentication_type: str(formData.nbn_authentication_type) || undefined,
        nbn_ip_address_type: str(formData.nbn_ip_address_type) || undefined,
        nbn_ip_address: str(formData.nbn_ip_address) || undefined,
        nbn_service_class: str(formData.nbn_service_class) || undefined,
        nbn_sla: str(formData.nbn_sla) || undefined,
        nbn_mapping: str(formData.nbn_mapping) || undefined,
        nbn_unid_port: str(formData.nbn_unid_port) || undefined,
      }),
      ...(formData.service_type === 'sonweb' && {
        primary_domain: str(formData.primary_domain) || undefined,
        web_server: str(formData.web_server) || undefined,
        web_hosting_plan: str(formData.web_hosting_plan) || undefined,
        panel_type: str(formData.panel_type) || undefined,
      }),
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!formData.service_id?.trim()) {
        throw new Error(isDomain ? 'Domain Name is required' : 'Service ID is required');
      }
      if (isDomain && !isLikelyFqdn(formData.service_id.trim())) {
        throw new Error('Enter a valid domain name (e.g. example.com.au)');
      }
      let typeSpecificPayload: Record<string, unknown>;
      try {
        typeSpecificPayload = buildTypeSpecificPayload();
      } catch (ve: unknown) {
        setError((ve as Error)?.message || 'Check custom fields');
        setLoading(false);
        return;
      }
      const payload = { ...buildPayload(), type_specific_data: typeSpecificPayload };
      if (isEdit && service) {
        await apiService.updateService(service.id, payload as AccountServiceUpdate);
      } else {
        await apiService.createAccountService(accountId, {
          ...payload,
          account_id: accountId,
        } as AccountServiceCreate);
      }
      await onSave();
      handleClose();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? (err as Error)?.message
        ?? 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const isNbn = formData.service_type === 'nbn';
  const isSonweb = formData.service_type === 'sonweb';
  const selectedTypeName =
    serviceTypeOptions.find((o) => o.code === formData.service_type)?.name || null;
  const isDomain = isDomainNameServiceType(formData.service_type, selectedTypeName);
  const domainServiceId = (formData.service_id || '').trim().toLowerCase();
  const domainLooksValid = !isDomain || domainServiceId === '' || isLikelyFqdn(domainServiceId);

  const typeSelectOptions: ServiceTypeOption[] = (() => {
    const codes = new Set(serviceTypeOptions.map((o) => o.code));
    const list = [...serviceTypeOptions];
    if (service?.service_type && !codes.has(service.service_type)) {
      list.unshift({
        code: service.service_type,
        name: `${service.service_type} (not in Settings)`,
        sort_order: -1,
      });
    }
    return list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  })();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Service' : 'Add New Service'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Common Fields
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={isDomain ? 'Domain Name' : 'Service ID'}
                value={formData.service_id}
                onChange={handleChange('service_id')}
                required
                placeholder={isDomain ? 'example.com.au' : 'SERVICE123456'}
                helperText={
                  isDomain
                    ? 'Used as the domain key for Synergy registry sync (e.g. example.com.au)'
                    : 'Unique service identifier'
                }
                error={isDomain && !domainLooksValid}
                inputProps={isDomain ? { autoCapitalize: 'none', spellCheck: false } : undefined}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Service Type"
                value={formData.service_type}
                onChange={handleServiceTypeChange}
                required
                helperText="Types are managed in Settings → Service Types"
              >
                {typeSelectOptions.map((o) => (
                  <MenuItem key={o.code} value={o.code}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={handleChange('status')}
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Upstream Carrier"
                value={formData.upstream_carrier}
                onChange={handleChange('upstream_carrier')}
              >
                <MenuItem value="">(None)</MenuItem>
                {upstreamCarriers.map((c) => (
                  <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={2}
                placeholder="Additional notes..."
              />
            </Grid>
          </Grid>

          {isNbn && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>NBN-Specific Fields</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Plan"
                    value={formData.nbn_plan_id}
                    onChange={handleChange('nbn_plan_id')}
                    helperText="Products from category Internet, subcategory nbn"
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {nbnPlans.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Technology Type"
                    value={formData.nbn_technology_type}
                    onChange={handleChange('nbn_technology_type')}
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {NBN_TECHNOLOGY_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Authentication Type"
                    value={formData.nbn_authentication_type}
                    onChange={handleChange('nbn_authentication_type')}
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {NBN_AUTHENTICATION_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="IP Address Type"
                    value={formData.nbn_ip_address_type}
                    onChange={handleChange('nbn_ip_address_type')}
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {NBN_IP_ADDRESS_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="IP Address"
                    value={formData.nbn_ip_address}
                    onChange={handleChange('nbn_ip_address')}
                    placeholder="e.g. 1.2.3.4"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Service Class"
                    value={formData.nbn_service_class}
                    onChange={handleChange('nbn_service_class')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="NBN SLA"
                    value={formData.nbn_sla}
                    onChange={handleChange('nbn_sla')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mapping"
                    value={formData.nbn_mapping}
                    onChange={handleChange('nbn_mapping')}
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {NBN_MAPPING_OPTIONS.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {(formData.nbn_technology_type === 'FTTP' || formData.nbn_technology_type === 'FW') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="UNID Port"
                      value={formData.nbn_unid_port}
                      onChange={handleChange('nbn_unid_port')}
                      helperText="FTTP and FW only"
                    >
                      <MenuItem value="">(None)</MenuItem>
                      {NBN_UNID_PORTS.map((p) => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WSM Service ID"
                    value={formData.wsm_service_id}
                    onChange={handleChange('wsm_service_id')}
                    placeholder="Vocus Members Portal ID"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="NBN Carrier ID"
                    value={formData.nbn_carrier_id}
                    onChange={handleChange('nbn_carrier_id')}
                    placeholder="LOC000045051838"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Vanity Identifier"
                    value={formData.nbn_vanity_identifier}
                    onChange={handleChange('nbn_vanity_identifier')}
                    placeholder="Vanity identifier"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="NBN AVC"
                    value={formData.nbn_avc}
                    onChange={handleChange('nbn_avc')}
                    placeholder="AVC000165144132"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="NBN POI"
                    value={formData.nbn_poi}
                    onChange={handleChange('nbn_poi')}
                    placeholder="2GOS"
                  />
                </Grid>
              </Grid>
            </>
          )}

          {isSonweb && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>Sonweb-Specific Fields</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Primary Domain"
                    value={formData.primary_domain}
                    onChange={handleChange('primary_domain')}
                    placeholder="example.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Web Server"
                    value={formData.web_server}
                    onChange={handleChange('web_server')}
                  >
                    {selectOptions('web_server', WEB_SERVERS).map((o) => (
                      <MenuItem key={o.value || 'blank'} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Web Hosting Plan"
                    value={formData.web_hosting_plan}
                    onChange={handleChange('web_hosting_plan')}
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {WEB_HOSTING_PLANS.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Panel Type"
                    value={formData.panel_type}
                    onChange={handleChange('panel_type')}
                  >
                    {selectOptions('panel_type', PANEL_TYPES).map((o) => (
                      <MenuItem key={o.value || 'blank'} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </>
          )}

          {formData.service_type === 'mobile' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>Mobile-Specific Fields</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Mobile plan"
                    value={typeSpecific.mobile_plan_id ?? ''}
                    onChange={(e) => setTypeSpecific((prev) => ({ ...prev, mobile_plan_id: e.target.value }))}
                    helperText="Products from category Mobile"
                  >
                    <MenuItem value="">(None)</MenuItem>
                    {typeSpecific.mobile_plan_id && !mobilePlans.some((p) => p.id === typeSpecific.mobile_plan_id) && (
                      <MenuItem value={typeSpecific.mobile_plan_id}>(Current plan)</MenuItem>
                    )}
                    {mobilePlans.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </>
          )}

          {dynamicFieldsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading service type fields…
              </Typography>
            </Box>
          )}

          {!dynamicFieldsLoading && dynamicFields.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>
                {serviceTypeOptions.find((o) => o.code === formData.service_type)?.name || 'Service type'} fields
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Defined in Settings → Service Types. Stored as custom field values on this service.
              </Typography>
              <Grid container spacing={2}>
                {dynamicFields.map((f) => {
                  const ftype = (f.field_type || 'text').toLowerCase();
                  const val = typeSpecific[f.field_key] ?? '';
                  const opts = [...(f.options || [])].sort(
                    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
                  );
                  const onDyn = (v: string) =>
                    setTypeSpecific((prev) => ({ ...prev, [f.field_key]: v }));

                  if (
                    formData.service_type === 'virtual_machine' &&
                    isVmPlanServiceField(f.field_key, f.label)
                  ) {
                    const selectVal = vmPlanSelectValue(val, vmPlanProducts);
                    const unknownLegacy =
                      selectVal &&
                      !vmPlanProducts.some((p) => p.id === selectVal) &&
                      !vmPlanProducts.some((p) => p.name === selectVal);
                    return (
                      <Grid item xs={12} md={6} key={f.id}>
                        <TextField
                          select
                          fullWidth
                          label={f.label}
                          value={unknownLegacy ? selectVal : selectVal || ''}
                          onChange={(e) => onDyn(e.target.value)}
                          required={!!f.required}
                          disabled={vmPlanProductsLoading}
                          helperText={
                            vmPlanProductsLoading
                              ? 'Loading plans from Orders…'
                              : vmPlanProducts.length === 0
                                ? `No products for category “${VM_ORDER_CATEGORY_SLUG}” / “${VM_ORDER_SUBCATEGORY_SLUG}”. Check Orders settings slugs.`
                                : undefined
                          }
                        >
                          {!f.required && <MenuItem value="">(None)</MenuItem>}
                          {vmPlanProducts.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                          {unknownLegacy && (
                            <MenuItem value={selectVal}>
                              (Legacy) {val}
                            </MenuItem>
                          )}
                        </TextField>
                      </Grid>
                    );
                  }

                  if (ftype === 'textarea') {
                    return (
                      <Grid item xs={12} key={f.id}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label={f.label}
                          value={val}
                          onChange={(e) => onDyn(e.target.value)}
                          required={!!f.required}
                        />
                      </Grid>
                    );
                  }
                  if (ftype === 'date') {
                    const iso = typeof val === 'string' ? val.slice(0, 10) : '';
                    return (
                      <Grid item xs={12} md={6} key={f.id}>
                        <TextField
                          fullWidth
                          type="date"
                          label={f.label}
                          value={iso}
                          onChange={(e) => onDyn(e.target.value)}
                          required={!!f.required}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    );
                  }
                  if (ftype === 'select' && opts.length > 0) {
                    return (
                      <Grid item xs={12} md={6} key={f.id}>
                        <TextField
                          select
                          fullWidth
                          label={f.label}
                          value={val}
                          onChange={(e) => onDyn(e.target.value)}
                          required={!!f.required}
                        >
                          {!f.required && <MenuItem value="">(None)</MenuItem>}
                          {opts.map((o) => (
                            <MenuItem key={o.id} value={o.value}>
                              {o.label?.trim() ? o.label : o.value}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    );
                  }
                  if (ftype === 'password') {
                    const touched = touchedPasswordFields.has(f.field_key);
                    return (
                      <Grid item xs={12} md={6} key={f.id}>
                        <TextField
                          fullWidth
                          type="password"
                          label={f.label}
                          value={touched ? val : ''}
                          onChange={(e) => {
                            if (!touched) setTouchedPasswordFields((prev) => new Set(prev).add(f.field_key));
                            onDyn(e.target.value);
                          }}
                          placeholder={val && !touched ? '••••••••' : undefined}
                          helperText={val && !touched ? 'A password is already saved — leave blank to keep it, or type a new one to replace it.' : undefined}
                        />
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} md={6} key={f.id}>
                      <TextField
                        fullWidth
                        label={f.label}
                        value={val}
                        onChange={(e) => onDyn(e.target.value)}
                        required={!!f.required}
                        type={ftype === 'number' ? 'text' : 'text'}
                        inputProps={ftype === 'number' ? { inputMode: 'decimal' } : undefined}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {isEdit && onRequestMove && (
            <Button
              startIcon={<MoveIcon />}
              onClick={() => onRequestMove()}
              disabled={loading}
              color="secondary"
            >
              Move to another account
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Service' : 'Add Service'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceFormDialog;
